// src/pages/Transactions.jsx
import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useApp } from '../context/AppContext'
import {
  MetricsGrid, MetricCard, CatPill, AcctChip, TypeBadge,
  SearchInput, FilterPills, EmptyState, ConfirmModal, SectionHead,
} from '../components/UI'
import { INC_CATEGORIES, EXP_CATEGORIES, PLANS } from '../utils/constants'
import { fmt, fmtDate, today } from '../utils/helpers'

const emptyForm = (type = 'expense') => ({
  date: today(), type, cat: '', acct: '', from: '', to: '', amt: '', note: '',
})

export default function Transactions() {
  const { t } = useTranslation()
  const {
    accounts, transactions, addTransaction, updateTransaction, deleteTransaction,
    acctName, acctColor, totalIncome, totalExpense, netSavings,
    currentPlan, txnLimitReached, txnRemaining,
  } = useApp()

  const [txnType, setTxnType] = useState('expense')
  const [form,    setForm]    = useState(emptyForm('expense'))
  const [editId,  setEditId]  = useState(null)
  const [search,  setSearch]  = useState('')
  const [filter,  setFilter]  = useState('All')
  const [delId,   setDelId]   = useState(null)
  const [formErr, setFormErr] = useState('')

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const setType = (tp) => { setTxnType(tp); setForm(emptyForm(tp)); setFormErr('') }

  const openEdit = (txn) => {
    setTxnType(txn.type)
    setForm({ date: txn.date, type: txn.type, cat: txn.cat || '', acct: txn.acct || '', from: txn.from || '', to: txn.to || '', amt: String(txn.amt), note: txn.note || '' })
    setEditId(txn.id); setFormErr('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const cancelEdit = () => { setEditId(null); setForm(emptyForm(txnType)); setFormErr('') }

  const validate = () => {
    if (!form.date) return t('transactions.date')
    if (!parseFloat(form.amt) || parseFloat(form.amt) <= 0) return t('common.error')
    if (form.type === 'transfer') {
      if (!form.from || !form.to) return t('transactions.from_account')
      if (form.from === form.to)  return t('common.error')
    } else {
      if (!form.cat)  return t('transactions.category')
      if (!form.acct) return t('transactions.account')
    }
    return ''
  }

  const save = () => {
    if (!editId && txnLimitReached) return
    const err = validate()
    if (err) { setFormErr(err); return }
    const data = { ...form, type: txnType, amt: parseFloat(form.amt) }
    if (editId) updateTransaction(editId, data)
    else        addTransaction(data)
    setEditId(null); setForm(emptyForm(txnType)); setFormErr('')
  }

  const filtered = useMemo(() =>
    transactions.filter(txn => {
      const ms = (txn.note || '').toLowerCase().includes(search.toLowerCase())
               || (txn.cat  || '').toLowerCase().includes(search.toLowerCase())
               || txn.ref.includes(search)
      const mf = filter === 'All' || txn.type === filter.toLowerCase()
      return ms && mf
    }).sort((a, b) => b.date.localeCompare(a.date) || b.ref.localeCompare(a.ref)),
    [transactions, search, filter]
  )

  const incCats = INC_CATEGORIES
  const expCats = EXP_CATEGORIES
  const cats    = txnType === 'income' ? incCats : txnType === 'transfer' ? [] : expCats
  const acctOpts = accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)

  return (
    <>
      <MetricsGrid>
        <MetricCard label={t('transactions.total_income')}   value={`₹${fmt(totalIncome)}`}  color="var(--green-600)" />
        <MetricCard label={t('transactions.total_expenses')} value={`₹${fmt(totalExpense)}`} color="var(--red-600)" />
        <MetricCard label={t('transactions.net_savings')}    value={`${netSavings >= 0 ? '+' : '−'}₹${fmt(Math.abs(netSavings))}`} color={netSavings >= 0 ? 'var(--brand-600)' : 'var(--red-600)'} />
        <MetricCard
          label={currentPlan.txnLimit === Infinity ? t('transactions.count') : `${t('transactions.count')} (${currentPlan.name})`}
          value={currentPlan.txnLimit === Infinity ? String(transactions.length) : `${transactions.length} / ${currentPlan.txnLimit}`}
          color={txnLimitReached ? 'var(--red-600)' : 'var(--gray-800)'}
        />
      </MetricsGrid>

      {/* Limit reached banner */}
      {txnLimitReached && (
        <div className="card" style={{ marginBottom: 14, background: 'var(--red-50)', borderColor: '#f0b8b8', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <i className="ti ti-lock" style={{ fontSize: 26, color: 'var(--red-600)', flexShrink: 0 }} aria-hidden="true" />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--red-800)' }}>
              {t('transactions.limit_reached_title', { limit: currentPlan.txnLimit, plan: currentPlan.name })}
            </div>
            <div style={{ fontSize: 12, color: 'var(--red-600)', marginTop: 2 }}>{t('transactions.limit_reached_sub')}</div>
          </div>
          <Link to="/pricing" className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
            <i className="ti ti-rocket" aria-hidden="true" /> {t('transactions.upgrade_now')}
          </Link>
        </div>
      )}

      {/* Soft warning */}
      {!txnLimitReached && currentPlan.txnLimit !== Infinity && txnRemaining <= 3 && (
        <div className="card" style={{ marginBottom: 14, background: 'var(--amber-50)', borderColor: '#f0d0a0', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: 'var(--amber-800)' }}>
          <i className="ti ti-alert-triangle" style={{ fontSize: 18, flexShrink: 0 }} aria-hidden="true" />
          {t('transactions.remaining_warning', { count: txnRemaining, plan: currentPlan.name, plural: txnRemaining !== 1 ? 's' : '' })}
          <Link to="/pricing" style={{ marginLeft: 'auto', color: 'var(--brand-600)', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            {t('transactions.view_plans')}
          </Link>
        </div>
      )}

      {/* Pricing teaser for free users */}
      {currentPlan.id === 'free' && !txnLimitReached && txnRemaining > 3 && (
        <Link to="/pricing" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', borderRadius: 10, textDecoration: 'none', background: 'linear-gradient(90deg, var(--brand-50), #fff)', border: '1px solid var(--brand-100)', fontSize: 13 }}>
          <i className="ti ti-sparkles" style={{ fontSize: 17, color: 'var(--brand-600)', flexShrink: 0 }} aria-hidden="true" />
          <span style={{ color: 'var(--gray-800)' }} dangerouslySetInnerHTML={{ __html: t('transactions.plan_teaser', { pro_price: PLANS.pro.priceLabel, premium_price: PLANS.premium.priceLabel }) }} />
          <span style={{ marginLeft: 'auto', color: 'var(--brand-600)', fontWeight: 600, whiteSpace: 'nowrap' }}>{t('transactions.view_plans')}</span>
        </Link>
      )}

      {/* Add/Edit form */}
      <div className="card" style={{ marginBottom: 14, opacity: (!editId && txnLimitReached) ? 0.55 : 1, pointerEvents: (!editId && txnLimitReached) ? 'none' : 'auto' }}>
        <SectionHead icon="ti-plus" title={editId ? t('transactions.edit') : t('transactions.add')} />

        <div className="seg-control">
          <button className={`seg-btn income${txnType === 'income' ? ' active' : ''}`}   onClick={() => setType('income')}>
            <i className="ti ti-trending-up" aria-hidden="true" /> {t('transactions.income')}
          </button>
          <button className={`seg-btn expense${txnType === 'expense' ? ' active' : ''}`} onClick={() => setType('expense')}>
            <i className="ti ti-trending-down" aria-hidden="true" /> {t('transactions.expense')}
          </button>
          <button className={`seg-btn transfer${txnType === 'transfer' ? ' active' : ''}`} onClick={() => setType('transfer')}>
            <i className="ti ti-arrows-right-left" aria-hidden="true" /> {t('transactions.transfer')}
          </button>
        </div>

        <div className="form-grid">
          <div className="field">
            <label className="field-label">{t('transactions.date')}</label>
            <input className="input" type="date" value={form.date} onChange={e => upd('date', e.target.value)} />
          </div>
          {txnType === 'transfer' ? (<>
            <div className="field">
              <label className="field-label">{t('transactions.from_account')}</label>
              <select className="select" value={form.from} onChange={e => upd('from', e.target.value)}>
                <option value="">{t('transactions.select')}</option>{acctOpts}
              </select>
            </div>
            <div className="field">
              <label className="field-label">{t('transactions.to_account')}</label>
              <select className="select" value={form.to} onChange={e => upd('to', e.target.value)}>
                <option value="">{t('transactions.select')}</option>{acctOpts}
              </select>
            </div>
          </>) : (<>
            <div className="field">
              <label className="field-label">{t('transactions.category')}</label>
              <select className="select" value={form.cat} onChange={e => upd('cat', e.target.value)}>
                <option value="">{t('transactions.select')}</option>
                {cats.map(c => <option key={c} value={c}>{t(`categories.${c}`, c)}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="field-label">{t('transactions.account')}</label>
              <select className="select" value={form.acct} onChange={e => upd('acct', e.target.value)}>
                <option value="">{t('transactions.select')}</option>{acctOpts}
              </select>
            </div>
          </>)}
          <div className="field">
            <label className="field-label">{t('transactions.amount')}</label>
            <input className="input" type="number" min="0" step="0.01" value={form.amt}
              onChange={e => upd('amt', e.target.value)} placeholder="0.00" style={{ textAlign: 'right' }} />
          </div>
          <div className="field col-span-2">
            <label className="field-label">{t('transactions.note')}</label>
            <textarea className="textarea" value={form.note} onChange={e => upd('note', e.target.value)} placeholder={t('transactions.note_placeholder')} />
          </div>
        </div>

        {formErr && (
          <div style={{ color: 'var(--red-600)', fontSize: 12, marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
            <i className="ti ti-alert-circle" aria-hidden="true" />{formErr}
          </div>
        )}

        <div className="btn-row">
          <button className="btn btn-primary" onClick={save} disabled={!editId && txnLimitReached}>
            <i className={`ti ti-${editId ? 'device-floppy' : 'check'}`} aria-hidden="true" />
            {editId ? t('transactions.update') : t('transactions.save')}
          </button>
          {editId && (
            <button className="btn btn-danger" onClick={cancelEdit}>
              <i className="ti ti-x" aria-hidden="true" /> {t('transactions.cancel')}
            </button>
          )}
        </div>
      </div>

      {/* Transaction list */}
      <div className="table-wrap">
        <div className="table-toolbar">
          <span className="table-toolbar-title">{filtered.length} {t('transactions.count')}</span>
          <SearchInput value={search} onChange={setSearch} placeholder={t('transactions.search_placeholder')} />
          <FilterPills
            options={['All', 'Income', 'Expense', 'Transfer']}
            labels={[t('transactions.all'), t('transactions.income'), t('transactions.expense'), t('transactions.transfer')]}
            value={filter}
            onChange={setFilter}
          />
        </div>

        {filtered.length === 0 ? (
          <EmptyState icon="ti-list" message={t('transactions.no_transactions')} sub={t('transactions.no_transactions_sub')} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>{t('transactions.date')}</th>
                  <th style={{ width: 72 }}>{t('transactions.ref')}</th>
                  <th style={{ width: 80 }}>{t('transactions.type')}</th>
                  <th style={{ width: 120 }}>{t('transactions.category')}</th>
                  <th style={{ width: 145 }}>{t('transactions.account')}</th>
                  <th style={{ width: 105, textAlign: 'right' }}>{t('transactions.amount')}</th>
                  <th style={{ width: 68 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(txn => {
                  const isTrf = txn.type === 'transfer'
                  const amtEl = isTrf
                    ? <span className="c-transfer">⇄ ₹{fmt(txn.amt)}</span>
                    : txn.type === 'income'
                      ? <span className="c-income">+₹{fmt(txn.amt)}</span>
                      : <span className="c-expense">−₹{fmt(txn.amt)}</span>
                  return (
                    <tr key={txn.id}>
                      <td className="mono">{fmtDate(txn.date)}</td>
                      <td className="mono">{txn.ref}</td>
                      <td><TypeBadge type={txn.type} /></td>
                      <td><CatPill cat={txn.cat} /></td>
                      <td>
                        {isTrf
                          ? <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>{acctName(txn.from)} → {acctName(txn.to)}</span>
                          : <AcctChip color={acctColor(txn.acct)} name={acctName(txn.acct)} />
                        }
                      </td>
                      <td style={{ textAlign: 'right' }}>{amtEl}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button className="icon-btn" onClick={() => openEdit(txn)} aria-label={t('common.edit')}>
                            <i className="ti ti-edit" aria-hidden="true" />
                          </button>
                          <button className="icon-btn danger" onClick={() => setDelId(txn.id)} aria-label={t('common.delete')}>
                            <i className="ti ti-trash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!delId}
        onClose={() => setDelId(null)}
        onConfirm={() => deleteTransaction(delId)}
        title={t('accounts.delete_confirm_title')}
        message="This will permanently remove the transaction and reverse the account balance."
        confirmLabel={t('common.delete')}
      />
    </>
  )
}
